import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import styles from './TabularDisplay.module.scss';
import { GroupedData } from "../../../../dataTypes/dataSetManagment/regroupData";

interface TabularDisplayProps<T> {
  title?: string;
  groupedData: GroupedData<T>;
  columns: {
    header: string;
    accessor: keyof T;
    width?: string;
    cellRenderer?: (value: any) => React.ReactNode;
    align?: 'left' | 'center' | 'right';
  }[];
  defaultHighlightColor?: string;
  rowDetailRenderer?: (row: T) => React.ReactNode;
  showGroupHeaders?: boolean;
  groupDisplayOrder?: 'normal' | 'reverse';
  hideEmptyGroups?: boolean;
  /** Tri appliqué au premier affichage (sinon l'ordre des données reçues). Un clic sur un en-tête le remplace. */
  defaultSort?: { column: keyof T; direction: 'asc' | 'desc' };
  onRowClick?: (row: T) => void;
  highlightClickableRows?: boolean;
  getRowTextColor?: (row: T) => string | undefined;
  defaultAllGroupsExpanded?: boolean;
  defaultExpandedGroups?: string[];
  singleRowExpansion?: boolean; 
  expandedRowBackgroundColor?: string; 
  expandedRowColor?: string;

  actionColumn?: {
    header: string;
    width?: string;
    align?: 'left' | 'center' | 'right';
    // MODIFIÉ: Ajout du paramètre isExpanded
    renderer: (row: T, rowIndex: number, groupKey?: string, isExpanded?: boolean) => React.ReactNode;
  };
}

export const TabularDisplay = <T,>({
  title,
  groupedData,
  columns,
  defaultHighlightColor = "#FFFFFF",
  rowDetailRenderer,
  showGroupHeaders = true,
  groupDisplayOrder = 'normal',
  hideEmptyGroups = false,
  onRowClick,
  highlightClickableRows = true,
  getRowTextColor,
  defaultAllGroupsExpanded = false,
  defaultExpandedGroups = [],
  singleRowExpansion = true,
  expandedRowColor, 
  expandedRowBackgroundColor, 
  actionColumn,
  defaultSort,
}: TabularDisplayProps<T>) => {
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [expandedRows, setExpandedRows] = useState<Record<string, Record<number, boolean>>>({});
  const [columnWidths, setColumnWidths] = useState<Record<string, string>>({});

  const [sortConfig, setSortConfig] = useState<{ column: keyof T | null, direction: 'asc' | 'desc' }>(
    defaultSort ?? { column: null, direction: 'asc' }
  );

  // Initialize missing group keys with default expansion state
  useEffect(() => {
    const groupKeys = Array.from(groupedData.groups).map(([key]) => key);
    setExpandedGroups((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const key of groupKeys) {
        if (!(key in prev)) {
          next[key] = (defaultExpandedGroups?.includes(key) ?? false) || defaultAllGroupsExpanded;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [groupedData, defaultAllGroupsExpanded, defaultExpandedGroups]);

  useEffect(() => {
    const calculateColumnWidths = () => {
      const widths: Record<string, number> = {};
      columns.forEach((col) => {
        if (col.width) {
          widths[col.accessor as string] = parseInt(col.width);
          return;
        }
        let maxWidth = 0;
        Array.from(groupedData.groups).forEach(([_, rows]) => {
          rows.forEach((row: T) => {
            const cellValue = row[col.accessor];
            const cellWidth = (cellValue?.toString().length || 0) * 8;
            if (cellWidth > maxWidth) {
              maxWidth = cellWidth;
            }
          });
        });
        widths[col.accessor as string] = maxWidth;
      });
      const widthsInPx: Record<string, string> = {};
      Object.keys(widths).forEach((key) => {
        widthsInPx[key] = `${widths[key]}px`;
      });
      setColumnWidths(widthsInPx);
    };

    calculateColumnWidths();
  }, [groupedData, columns]);

  const toggleGroupVisibility = (groupKey: string) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupKey]: !prev[groupKey],
    }));
  };

  const toggleRowVisibility = (groupKey: string, rowIndex: number) => {
    setExpandedRows((prev) => {
      const currentRowExpanded = prev[groupKey]?.[rowIndex] ?? false;
      
      if (singleRowExpansion) {
        if (!currentRowExpanded) {
          return {
            [groupKey]: {
              [rowIndex]: true
            }
          };
        } else {
          return {
            [groupKey]: {
              [rowIndex]: false
            }
          };
        }
      } else {
        return {
          ...prev,
          [groupKey]: {
            ...prev[groupKey],
            [rowIndex]: !currentRowExpanded,
          },
        };
      }
    });
  };

  const sortRows = (rows: T[]): T[] => {
    if (!sortConfig.column) return rows;

    return [...rows].sort((a, b) => {
      const aVal = a[sortConfig.column!];
      const bVal = b[sortConfig.column!];

      if (aVal == null) return 1;
      if (bVal == null) return -1;

      // Texte : ordre alphabétique sans casse ni accent, et numérique pour « #9 » avant « #18 ».
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        const cmp = aVal.localeCompare(bVal, 'fr', { numeric: true, sensitivity: 'base' });
        return sortConfig.direction === 'asc' ? cmp : -cmp;
      }

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const handleSort = (col: keyof T) => {
    setSortConfig((prev) => {
      if (prev.column === col) {
        return { column: col, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { column: col, direction: 'asc' };
    });
  };

  const groupsToDisplay = groupDisplayOrder === 'reverse'
    ? Array.from(groupedData.groups).reverse()
    : Array.from(groupedData.groups);

  const filteredGroups = hideEmptyGroups
    ? groupsToDisplay.filter(([_, rows]) => rows.length > 0)
    : groupsToDisplay;

  const renderTableHeader = () => (
    <tr>
      {columns.map((col) => (
        <th
          key={String(col.accessor)}
          style={{ width: col.width || columnWidths[col.accessor as string], cursor: 'pointer' }}
          onClick={() => handleSort(col.accessor)}
        >
          {col.header}
          {sortConfig.column === col.accessor && (
            <span style={{ marginLeft: 5 }}>
              {sortConfig.direction === 'asc' ? '▲' : '▼'}
            </span>
          )}
        </th>
      ))}
      {actionColumn && (
        <th
          style={{ 
            width: actionColumn.width || 'auto',
            textAlign: actionColumn.align || 'left'
          }}
        >
          {actionColumn.header}
        </th>
      )}
    </tr>
  );

  const renderTableRow = (row: T, rowIndex: number, groupKey: string = '__ungrouped__') => {
    const isRowExpanded = expandedRows[groupKey]?.[rowIndex] ?? false;
    const isClickable = !!onRowClick || !!rowDetailRenderer;
    const rowTextColor = getRowTextColor?.(row);

    const rowStyle: React.CSSProperties = {
      ...(rowTextColor ? { color: rowTextColor } : {}),
      ...(isRowExpanded && expandedRowBackgroundColor ? { backgroundColor: expandedRowBackgroundColor, color: expandedRowColor  } : {})
    };

    return (
      <React.Fragment key={rowIndex}>
        <tr
          className={classNames(
            styles.row,
            { [styles.rowWithDetails]: !!rowDetailRenderer },
            { [styles.rowClickable]: isClickable && highlightClickableRows }
          )}
          style={rowStyle}
          onClick={(e) => {
            if ((e.target as HTMLElement).closest('button,a,input,select,textarea')) return;
            if (rowDetailRenderer) toggleRowVisibility(groupKey, rowIndex);
            if (onRowClick) onRowClick(row);
          }}
        >
          {columns.map((col) => (
            <td
              key={String(col.accessor)}
              style={{
                width: col.width || columnWidths[col.accessor as string],
                textAlign: col.align || 'left'
              }}
            >
              {col.cellRenderer
                ? col.cellRenderer(row[col.accessor])
                : String(row[col.accessor])}
            </td>
          ))}
          {actionColumn && (
            <td
              style={{
                width: actionColumn.width || 'auto',
                textAlign: actionColumn.align || 'left'
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              {/* MODIFIÉ: Passage du paramètre isExpanded au renderer */}
              {actionColumn.renderer(row, rowIndex, groupKey, isRowExpanded)}
            </td>
          )}
        </tr>
        {isRowExpanded && rowDetailRenderer && (
          <tr className={styles.rowDetail}>
            <td colSpan={columns.length + (actionColumn ? 1 : 0)}>
              {rowDetailRenderer(row)}
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  return (
    <div>
      {title && <h2>{title}</h2>}
      {showGroupHeaders ? (
        filteredGroups.map(([groupKey, rows]) => {
          const isExpanded = expandedGroups[groupKey] ?? false;
          const groupHeaderContent = groupedData.renderer
            ? groupedData.renderer(groupKey)
            : groupKey;

          const rowsInOrder = groupDisplayOrder === 'reverse' ? [...rows].reverse() : rows;
          const sortedRows = sortRows(rowsInOrder);

          return (
            <div key={groupKey} className={styles.container}>
              <div
                className={classNames(styles.groupHeader, { [styles.expanded]: isExpanded })}
                style={{ '--highlight-color': defaultHighlightColor } as React.CSSProperties}
                onClick={() => toggleGroupVisibility(groupKey)}
              >
                {groupHeaderContent} <span className={styles.stats}>({rows.length} items)</span>
              </div>
              {isExpanded && (
                <table className={styles.table}>
                  <thead>
                    {renderTableHeader()}
                  </thead>
                  <tbody>
                    {sortedRows.map((row: T, rowIndex: number) => 
                      renderTableRow(row, rowIndex, groupKey)
                    )}
                  </tbody>
                </table>
              )}
            </div>
          );
        })
      ) : (
        <table className={styles.table}>
          <thead>
            {renderTableHeader()}
          </thead>
          <tbody>
            {sortRows(Array.from(groupedData.groups).flatMap(([, rows]) => rows))
              .map((row: T, rowIndex: number) => 
                renderTableRow(row, rowIndex)
              )}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default TabularDisplay;